import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBClearButton } from '@shared/components/button';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTypographyStylesRaw } from '@shared/components/typography';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { LinkedCard } from '.';

interface Props {
  card: LinkedCard;
  onPress?(card: LinkedCard): void;
  onRemove?(): void;
}

const ICON_SIZE = grid(3);

export function MBInlineCard({ card, onPress, onRemove }: Props) {
  const colors = React.useContext(ColorThemeContext);
  const onClickCard = useMemo(() => {
    if (onPress) {
      return () => {
        onPress?.(card);
      };
    }
  }, [card, onPress]);

  const cardPill = useMemo(() => {
    return (
      <MBTouchableOpacity onPress={onClickCard} style={styles.container}>
        <MBView style={styles.card}>
          <View paddingRight={1}>
            <MBIconV2 name="arrow-link-out" color={colors.foregroundActive} size={ICON_SIZE} />
          </View>
          <MBView style={styles.cardName}>
            <MBTextBody1 color={colors.foregroundActive}>{card.name}</MBTextBody1>
          </MBView>
        </MBView>
      </MBTouchableOpacity>
    );
  }, [card.name, colors.foregroundActive, onClickCard]);

  if (!onRemove) {
    return cardPill;
  }

  return (
    <View row style={{ justifyContent: 'space-between' }}>
      {cardPill}
      <View fill />
      <MBClearButton onPress={onRemove} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    flexWrap: 'wrap',
    maxWidth: '75%',
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    border: `1px solid ${Colors.v2.gray10}`,
    borderRadius: grid(1),
    marginBottom: grid(0.5),
    marginRight: grid(0.5),
    boxShadow: '2px 2px 8px 0 rgba(0,0,0,0.1)',
    alignItems: 'flex-start',
    backgroundColor: 'white',
    paddingLeft: grid(1),
    paddingRight: grid(2),
    paddingTop: grid(1),
    paddingBottom: grid(1),
  },
  cardName: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: '1',
    paddingTop: 2,
    ...MBTypographyStylesRaw.default,
    lineHeight: '24px',
  } as any,
});
